import { useCallback } from "react";

import { useAttachmentPolicy, type AttachmentCreate } from "~/entities/chat";
import { useDraftChatStore } from "~/shared/store/useDraftChatStore";
import type { PendingUpload } from "~/shared/store/types";

import { buildPendingSeed, uploadPendingFile } from "./upload-strategies";

export type { PendingUpload } from "~/shared/store/types";

const EMPTY_PENDING: readonly PendingUpload[] = Object.freeze([]);

export function useUploadAttachment(chatId: string | null) {
    const items = useDraftChatStore((s) =>
        chatId ? (s.pendingByChat[chatId] ?? EMPTY_PENDING) : EMPTY_PENDING,
    );
    const addPending = useDraftChatStore((s) => s.addPending);
    const updatePending = useDraftChatStore((s) => s.updatePending);
    const removePending = useDraftChatStore((s) => s.removePending);
    const reorderPending = useDraftChatStore((s) => s.reorderPending);
    const clearPending = useDraftChatStore((s) => s.clearPending);

    const policyQuery = useAttachmentPolicy();
    const multipartThreshold = policyQuery.data?.multipartThresholdBytes ?? Number.POSITIVE_INFINITY;

    // `seedPatch` дописывается в каждый seed до постановки в очередь: так
    // загрузка в роль-слот сразу помечается ролью, без второго
    // прохода по store после старта аплоада.
    const upload = useCallback(
        async (files: File[], seedPatch?: Partial<PendingUpload>): Promise<AttachmentCreate[]> => {
            if (!chatId) throw new Error("chatId is required before uploading attachments");

            const seeds: PendingUpload[] = files.map((file) => ({
                ...buildPendingSeed(file),
                ...seedPatch,
            }));
            addPending(chatId, seeds);

            const created: AttachmentCreate[] = [];
            for (let i = 0; i < files.length; i += 1) {
                const file = files[i];
                const seed = seeds[i];
                if (!file || !seed) continue;
                const attachment = await uploadPendingFile(
                    file,
                    seed,
                    { chatId, updatePending },
                    multipartThreshold,
                );
                if (attachment) created.push(attachment);
            }

            return created;
        },
        [chatId, addPending, updatePending, multipartThreshold],
    );

    const remove = useCallback(
        (localId: string) => {
            if (!chatId) return;
            removePending(chatId, localId);
        },
        [chatId, removePending],
    );

    const reorder = useCallback(
        (fromIdx: number, toIdx: number) => {
            if (!chatId) return;
            reorderPending(chatId, fromIdx, toIdx);
        },
        [chatId, reorderPending],
    );

    const reset = useCallback(() => {
        if (!chatId) return;
        clearPending(chatId);
    }, [chatId, clearPending]);

    return { items, upload, remove, reorder, reset } as const;
}
